import { gatewayFetch, type GatewayRequestInit } from './gatewayFetch';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type RequestParams = {
  query?: GatewayRequestInit['query'];
  headers?: HeadersInit;
  body?: unknown;
  init?: Omit<GatewayRequestInit, 'method' | 'headers' | 'body' | 'query'>;
};

export type HttpClientConfig = {
  // Routes every request through `${gateway}/services/<service>`. Omit to hit the gateway root (auth/users).
  service?: string;
  // Base alternativa (ex.: '/api' no preview). Quando presente, substitui o
  // gateway E o prefixo de service: a chamada vai direto ao backend ao lado.
  baseUrl?: string;
  // Set false for public/no-refresh endpoints — skips the 401 refresh-and-retry.
  refreshToken?: boolean;
  // Desembrulha o envelope { success, data } do backend deste produto.
  unwrapEnvelope?: boolean;
};

const SERVICES_PREFIX = 'services';

type SuccessEnvelope<T> = { success: true; data: T };

/** O Nest do boilerplate responde { success, data, ... }; as telas querem o data. */
function unwrap<T>(payload: unknown): T {
  if (payload !== null && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

export class HttpClient {
  private readonly service?: string;
  private readonly baseUrl?: string;
  private readonly refreshToken: boolean;
  private readonly unwrapEnvelope: boolean;

  // Accepts a config object, or a boolean for the backwards-compatible `new HttpClient(false)` form.
  constructor(config: HttpClientConfig | boolean = {}) {
    const normalized: HttpClientConfig = typeof config === 'boolean' ? { refreshToken: config } : config;
    this.service = normalized.service;
    this.baseUrl = normalized.baseUrl;
    this.refreshToken = normalized.refreshToken ?? true;
    this.unwrapEnvelope = normalized.unwrapEnvelope ?? false;
  }

  private resolvePath(path: string): string {
    const normalized = path.replace(/^\/+/, '');
    // Com base propria a chamada NAO passa pelo gateway, entao nao leva o
    // prefixo de service: `/api/suppliers` e nao `/api/services/x/suppliers`.
    if (!this.service || this.baseUrl) {
      return normalized;
    }
    return `${SERVICES_PREFIX}/${this.service}/${normalized}`;
  }

  private request<T>(method: HttpMethod, path: string, params: RequestParams = {}) {
    const { query, headers, body, init } = params;

    const isJsonBody = body !== undefined && !(body instanceof FormData);
    const mergedHeaders = new Headers(headers);
    if (isJsonBody && !mergedHeaders.has('Content-Type')) {
      mergedHeaders.set('Content-Type', 'application/json');
    }

    return this.send<T>(this.resolvePath(path), {
      ...init,
      method,
      query,
      headers: mergedHeaders,
      body: body === undefined ? undefined : isJsonBody ? JSON.stringify(body) : (body as BodyInit),
      baseUrl: this.baseUrl,
      cache: init?.cache ?? 'no-store',
      __refreshToken: this.refreshToken,
    } as GatewayRequestInit & { __refreshToken: boolean });
  }

  private async send<T>(path: string, init: GatewayRequestInit): Promise<T> {
    const payload = await gatewayFetch<T>(path, init);
    return this.unwrapEnvelope ? unwrap<T>(payload) : payload;
  }

  get<T>(path: string, params: Omit<RequestParams, 'body'> = {}) {
    return this.request<T>('GET', path, params);
  }

  post<T>(path: string, params: RequestParams = {}) {
    return this.request<T>('POST', path, params);
  }

  put<T>(path: string, params: RequestParams = {}) {
    return this.request<T>('PUT', path, params);
  }

  patch<T>(path: string, params: RequestParams = {}) {
    return this.request<T>('PATCH', path, params);
  }

  delete<T>(path: string, params: RequestParams = {}) {
    return this.request<T>('DELETE', path, params);
  }
}

// Gateway root client — auth/users endpoints (`/auth/*`, `/users/*`).
export const gatewayHttpClient = new HttpClient(true);

const serviceClients = new Map<string, HttpClient>();

// Cached gateway client scoped to a backend service: every call goes to `${gateway}/services/<service>`.
export function gatewayServiceClient(service: string, refreshToken = true): HttpClient {
  const key = `${service}::${refreshToken ? 'r' : 'nr'}`;
  const cached = serviceClients.get(key);
  if (cached) {
    return cached;
  }

  const client = new HttpClient({ service, refreshToken });
  serviceClients.set(key, client);
  return client;
}

/**
 * O backend DESTE produto (apps/server), que e um microservico como os outros:
 * em producao ele vive atras do gateway e a chamada leva o prefixo
 * `services/<nome>`, exatamente como qualquer outro app da casa consome o uflow
 * ou o upick.
 *
 * A UNICA coisa que muda por ambiente e a BASE:
 *  - producao / staging: o gateway (NEXT_PUBLIC_SERVICE_BASE_URL vazio)
 *  - preview do Brainwave e dev local: '/api', que o Next encaminha para o Nest
 *    que roda ao lado — assim a sessao exercita a API que o agente acabou de
 *    escrever, e nao a versao ja deployada.
 *
 * O codigo das telas e dos repositories e o mesmo nos dois casos: quem escreve
 * uma tela nunca precisa saber onde o backend esta.
 */
const SERVICE_NAME = process.env.NEXT_PUBLIC_SERVICE_NAME ?? '';
const SERVICE_BASE_URL = process.env.NEXT_PUBLIC_SERVICE_BASE_URL ?? '';

export const appServiceClient = new HttpClient({
  service: SERVICE_NAME === '' ? undefined : SERVICE_NAME,
  baseUrl: SERVICE_BASE_URL === '' ? undefined : SERVICE_BASE_URL,
  unwrapEnvelope: true,
});
